import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { IBase } from 'src/app/models';
import { InputType } from 'src/app/models/enums';
import { UtilsService } from 'src/app/services';
import { images } from 'src/images';
import { TooltipPosition } from '../../tooltip/tooltip-position';
import { LiteralService } from 'src/app/services/literal/literal.service';

@Directive({ standalone: false })
export abstract class MultiSelectBase implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() title: string;
  @Input() elements: any[] = [];
  @Input() values?: any[];
  @Input() required?: boolean = false;
  @Input() disabled?: boolean = false;
  @Input() width = 'auto';
  @Input() showSearch: boolean = true;
  @Input() dropdownMaxHeight?: string;
  @Input() hasErrors?: boolean = false;
  @Input() error?: string = '';
  @Input() dropdownParent?: string;
  @Input() showDropDown?: boolean;
  @Input() translateValue?: boolean;
  @Input() orderById?: boolean = true;
  @Input() placeholder?: string;
  @Input() showItemEdit?: boolean = false;
  @Input() iconLeft?: boolean = false;
  @Input() pinSelectedToTop?: boolean = false;
  @Input() showSelectAll?: boolean = false;
  @Input() selectAllLabel = 'allAreas';
  @Input() selectAllChecksItems?: boolean = false;
  @Input() emitOnChange?: boolean = false;
  @Output() closeEventEmitter = new EventEmitter();
  @Output() openEventEmitter = new EventEmitter();
  @Output() onBlurEventEmitter = new EventEmitter();
  @Output() changeEventEmitter = new EventEmitter();
  @Output() editClicked = new EventEmitter<any>();

  public isHidden: boolean = true;
  public searchableText: string = '';
  public isSelectAll: boolean = false;

  public images = images;
  public InputType = InputType;
  public TooltipPosition = TooltipPosition;

  public textValues = '';

  @ViewChild('search') search: ElementRef;
  @ViewChild('input') inputElement?: ElementRef<HTMLElement>;
  @ViewChild('dropdown') dropdownElement?: ElementRef<HTMLElement>;

  public inputs: HTMLElement[] = [];
  public dropdowns: HTMLElement[] = [];
  public texts: HTMLElement[] = [];

  public originalData: any[] = [];

  onChange(event: any) {
    if (!this.values) return;

    const itemValue = event.target.dataset?.value;
    if (!itemValue || itemValue === 'all') return;

    if (this.isSelectAll) {
      this.isSelectAll = false;
      this.values.splice(0, this.values.length);
    }

    if (this.orderById) this.values.sort((a: any, b: any) => a.id! - b.id! || a - b);

    const element = this.elements?.find((el: any) => el.id == itemValue || el == itemValue);
    if (!element) return;

    const index = this.values.findIndex((val: any) => val.id == element.id || val == element);

    if (event.target.checked) {
      if (!this.values.some((value) => value.id === element.id)) this.values.push(element);
    } else if (index !== -1) {
      this.values.splice(index, 1);
      if (this.showSelectAll && !this.selectAllChecksItems && this.values.length === 0) this.isSelectAll = true;
    }
    if (this.emitOnChange) this.changeEventEmitter.emit(event);

    setTimeout(() => this.getTextFromArray());
  }

  @HostListener('document:click', ['$event'])
  clickout(event: any) {
    if (!this.eRef.nativeElement.contains(event.target)) {
      if (!this.isHidden) {
        this.onBlurEventEmitter.emit(event);
      }
    }
  }

  private unlistenWindowClick?: () => void;

  constructor(
    renderer: Renderer2,
    public utilsService: UtilsService,
    public literalService: LiteralService,
    protected eRef: ElementRef,
  ) {
    this.unlistenWindowClick = renderer.listen('window', 'click', (e: Event) => {
      if (this.inputs[0] !== null && this.dropdowns[0] !== null) {
        const close = utilsService.closeComponent(e, this.inputs, this.dropdowns, this.title);
        if (close) {
          const wasOpen = !this.isHidden;
          this.isHidden = true;
          this.searchableText = '';
          this.elements = this.originalData;
          this.getTextFromArray();
          if (wasOpen) this.closeEventEmitter.emit();
        }
      } else {
        const dropdownParent = document.getElementById(this.dropdownParent!);
        const clickInside = utilsService.openComponentOnlyWithDropdown(
          e,
          dropdownParent!,
          this.dropdowns,
          this.title,
        );
        if (clickInside) {
          this.openEventEmitter!.emit();
        } else {
          this.closeEventEmitter!.emit();
        }
      }
    });
    this.getTextFromArray();
  }

  ngOnInit(): void {
    this.originalData = this.elements!;
    this.getTextFromArray();
  }

  // its own elements, never a namesake of another instance the document also holds
  ngAfterViewInit(): void {
    this.inputs.push(this.inputElement?.nativeElement ?? (null as unknown as HTMLElement));
    this.dropdowns.push(this.dropdownElement?.nativeElement ?? (null as unknown as HTMLElement));
  }

  ngOnDestroy(): void {
    this.unlistenWindowClick?.();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['elements']) {
      this.originalData = changes['elements'].currentValue;
    }
    if (changes['values']) {
      this.getTextFromArray();
    }
  }

  public checked(item: any) {
    if (this.isSelectAll) return false;
    return this.values?.some((value) =>
      value && typeof value === 'object' ? value.id == item.id : value === item,
    );
  }

  get selectedElements(): any[] {
    return this.elements?.filter((item) => this.checked(item)) ?? [];
  }

  get unselectedElements(): any[] {
    return this.elements?.filter((item) => !this.checked(item)) ?? [];
  }

  get pinnedElements(): any[] {
    return [...this.selectedElements, ...this.unselectedElements];
  }

  get selectAllChecked(): boolean {
    if (this.selectAllChecksItems) return !!this.elements?.length && this.elements.every((item) => this.checked(item));
    return this.isSelectAll;
  }

  toggleSelectAll(event: any) {
    if (!this.values) return;

    if (this.selectAllChecksItems) {
      this.values.splice(0, this.values.length, ...(event.target.checked ? (this.elements ?? []) : []));
      if (this.emitOnChange) this.changeEventEmitter.emit(event);
      setTimeout(() => this.getTextFromArray());
      return;
    }

    if (!event.target.checked) {
      event.target.checked = true;
      return;
    }

    this.isSelectAll = true;
    this.values.splice(0, this.values.length, ...(this.elements ?? []));

    if (this.emitOnChange) this.changeEventEmitter.emit(event);

    setTimeout(() => this.getTextFromArray());
  }

  public filterElements() {
    if (this.searchableText.length > 0) {
      const q = this.searchableText.toLowerCase();
      this.elements = this.originalData?.filter((value: any) => {
        const name = (value?.name ?? '').toString().toLowerCase();
        const code = (value?.code ?? '').toString().toLowerCase();
        return name.includes(q) || code.includes(q);
      });
    } else {
      this.elements = this.originalData;
    }
  }

  getValue(item: any) {
    const value = typeof item === 'object' ? item.name || item.id || item.code : item;
    return this.translateValue ? this.literalService.get(value, true) : value;
  }

  getTextFromArray() {
    setTimeout(() => {
      if (this.isSelectAll) {
        this.textValues = this.literalService.get(this.selectAllLabel, true);
        return;
      }

      this.textValues = '';

      if (!this.elements?.length) {
        this.textValues = this.placeholder || '-';
        return;
      }

      if (this.orderById) this.elements.sort((a: IBase, b: IBase) => a.id! - b.id!);

      const selectedTexts = this.elements
        .filter((el) => this.checked(el))
        .map((el) => this.getValue(el));

      this.textValues = selectedTexts.length ? selectedTexts.join(', ') : this.placeholder || '-';
    });
  }
}
